import { useEffect } from "react";
import L from "leaflet";
import { CircleMarker, MapContainer, Marker, Polyline, Popup, TileLayer, useMap } from "react-leaflet";
import { fmtTime } from "@/lib/format";

const TILES = "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png";
const ATTR = '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors';

export type LivePos = { deviceId: string; vehicleId?: string; plate?: string; lat: number; lon: number; speed?: number; course?: number; timestamp?: string; online?: boolean; make?: string; model?: string };

function cabIcon(p: LivePos, selected: boolean) {
  const color = !p.online ? "#64748b" : (p.speed || 0) > 3 ? "#3b82f6" : "#10b981";
  const html = `<div class="cab-marker ${p.online ? "" : "offline"}"><svg width="30" height="30" viewBox="0 0 30 30" style="transform: rotate(${p.course || 0}deg)">
    <circle cx="15" cy="15" r="13" fill="${selected ? "#f59e0b" : "#0a0d14"}" stroke="${color}" stroke-width="2"/>
    <path d="M15 5 L21 19 L15 16 L9 19 Z" fill="${color}"/></svg></div>`;
  return L.divIcon({ html, className: "", iconSize: [30, 30], iconAnchor: [15, 15] });
}

function Fit({ bounds }: { bounds: L.LatLngBoundsExpression | null }) {
  const map = useMap();
  useEffect(() => {
    if (bounds) map.fitBounds(bounds, { padding: [40, 40], maxZoom: 15 });
  }, [bounds, map]);
  return null;
}

export type TrackPoint = [string, number, number, number];

export function FleetMap({ positions = [], track, pins = [], stops = [], selectedId, onSelect, fit = true, className, center, replay }: {
  positions?: LivePos[]; track?: TrackPoint[]; pins?: { lat: number; lon: number; label: string; color?: string }[];
  stops?: { lat: number; lon: number; durationSec: number }[]; selectedId?: string | null; onSelect?: (id: string) => void; fit?: boolean; className?: string; center?: [number, number];
  replay?: { lat: number; lon: number; speed: number; ts: string } | null;
}) {
  let bounds: L.LatLngBoundsExpression | null = null;
  const pts: [number, number][] = [...positions.map((p) => [p.lat, p.lon] as [number, number]), ...(track || []).map((t) => [t[1], t[2]] as [number, number]), ...pins.map((p) => [p.lat, p.lon] as [number, number])];
  if (fit && pts.length > 1) bounds = L.latLngBounds(pts);
  if (fit && pts.length === 1) bounds = L.latLngBounds([pts[0], pts[0]]);
  return (
    <div data-testid="fleet-map" className={className || "h-full w-full"}>
      <MapContainer center={center || [51.507, -0.1246]} zoom={12} className="h-full w-full rounded-lg" zoomControl={true}>
        <TileLayer url={TILES} attribution={ATTR} />
        <Fit bounds={bounds} />
        {track && track.length > 1 && <Polyline positions={track.map((t) => [t[1], t[2]])} pathOptions={{ color: "#3b82f6", weight: 4, opacity: 0.9 }} />}
        {track && track.length > 0 && (
          <>
            <CircleMarker center={[track[0][1], track[0][2]]} radius={7} pathOptions={{ color: "#10b981", fillColor: "#10b981", fillOpacity: 1 }}><Popup>GPS start · {fmtTime(track[0][0])}</Popup></CircleMarker>
            <CircleMarker center={[track[track.length - 1][1], track[track.length - 1][2]]} radius={7} pathOptions={{ color: "#f43f5e", fillColor: "#f43f5e", fillOpacity: 1 }}><Popup>GPS end · {fmtTime(track[track.length - 1][0])}</Popup></CircleMarker>
          </>
        )}
        {stops.map((s, i) => (
          <CircleMarker key={i} center={[s.lat, s.lon]} radius={5} pathOptions={{ color: "#f59e0b", fillColor: "#f59e0b", fillOpacity: 0.8 }}><Popup>Stop · {Math.round(s.durationSec / 60)} min</Popup></CircleMarker>
        ))}
        {pins.map((p, i) => (
          <CircleMarker key={`pin-${i}`} center={[p.lat, p.lon]} radius={9} pathOptions={{ color: p.color || "#a855f7", fillColor: p.color || "#a855f7", fillOpacity: 0.55, weight: 2, dashArray: "3 3" }}><Popup>{p.label}</Popup></CircleMarker>
        ))}
        {replay && <CircleMarker center={[replay.lat, replay.lon]} radius={11} pathOptions={{ color: "#f59e0b", fillColor: "#f59e0b", fillOpacity: 0.9, weight: 3 }}><Popup>{Math.round(replay.speed)} km/h · {fmtTime(replay.ts)}</Popup></CircleMarker>}
        {positions.map((p) => (
          <Marker key={p.deviceId} position={[p.lat, p.lon]} icon={cabIcon(p, selectedId === p.vehicleId)} eventHandlers={{ click: () => onSelect?.(p.vehicleId || p.deviceId) }}>
            <Popup>
              <div data-testid={`map-popup-${p.plate}`} className="space-y-1">
                <div className="font-mono font-semibold text-sm">{p.plate}</div>
                <div className="text-slate-300">{p.make} {p.model}</div>
                <div className="font-mono text-slate-400">{Math.round(p.speed || 0)} km/h · {fmtTime(p.timestamp)}</div>
              </div>
            </Popup>
          </Marker>
        ))}
      </MapContainer>
    </div>
  );
}
